import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  concatMap,
  debounceTime,
  defaultIfEmpty,
  defer,
  EMPTY,
  filter,
  finalize,
  first,
  map,
  mergeMap,
  Observable,
  of,
  OperatorFunction,
  race,
  retry,
  share,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';

import { LayoutHistory, runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  CanvasElement,
  CanvasNamespaceId,
  LayoutData,
  LayoutSyncPayload,
  NAV_MENU_ITEMS_LAYOUT_TYPE,
} from '@blaze-runtime/engine-domain';
import { logPageLayout } from '@blaze-runtime/engine-util';
import { PageInfo, UpdateProjectViewResponseV2 } from '@blaze-shared/api-contracts-domain';
import { ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { UserAccessRolesService } from '@blaze-shared/auth-data-access';
import { NavMenu } from '@blaze-shared/elements-domain';
import { sentryLog } from '@blaze-shared/logger-util';
import { NotificationService } from '@blaze-shared/platform-data-access';
import { ProjectsApiService } from '@blaze-shared/projects-data-access';
import { fromStyles } from '@blaze-shared/style-state-feature';
import { StyleSheet } from '@blaze-shared/theming-domain';
import { Connection } from '@blaze-shared/workflow-domain';

import { fromElements, RuntimeFacade } from '../+state';
import { ElementsFacade } from '../+state/elements/elements.facade';
import { LayoutsApiDataResponse } from '../data/layouts-api.service';
import { calcLayoutSaveData, LayoutSavingData } from '../utils/elements/layout-saving';
import { LayoutValidationError, validateLayout } from '../utils/layout/validators';

import { ElementDefinitionsService, GetPropsExtractorFn } from './element-definitions.service';
import { NavMenuItemsService } from './nav-menu-items.service';
import { ElementStylesService } from './styles';

export const SYNC_DEBOUNCE_TIME = 3000;
export const SYNC_RETRY_INTERVAL = 3000;

@Injectable({ providedIn: 'root' })
export class LayoutSyncService {
  private readonly elementsFacade = inject(ElementsFacade);
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly elementStylesService = inject(ElementStylesService);
  private readonly layoutHistory = inject(LayoutHistory);
  private readonly navMenuItemsService = inject(NavMenuItemsService);
  private readonly notificationService = inject(NotificationService);
  private readonly projectsApiService = inject(ProjectsApiService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly projectContextFacade = inject(ProjectContextFacade);
  private readonly userAccessRolesService = inject(UserAccessRolesService);
  private readonly store = inject(Store);
  private readonly styleSheetsFacade = inject(fromStyles.StyleSheetsFacade);

  private readonly manualSync$$ = new Subject<string>();
  private readonly manualSync$: Observable<{ value: boolean; syncId: string | undefined }>;

  private readonly isApiCallInProgress$$ = new BehaviorSubject<boolean>(false);
  private readonly isManualSyncing$$ = new BehaviorSubject<boolean>(false);
  private readonly isDragging$$ = new BehaviorSubject<boolean>(false);
  private readonly getPropsExtractorFn = this.elementDefinitionsService.getPropsExtractor.bind(
    this.elementDefinitionsService,
  );
  readonly isApiCallInProgress$ = this.isApiCallInProgress$$.asObservable();

  readonly isManualSyncing$ = this.isManualSyncing$$.asObservable();

  constructor() {
    this.manualSync$ = this.manualSync$$.pipe(
      tap(() => this.isManualSyncing$$.next(true)),
      concatMap((syncId) =>
        this.isApiCallInProgress$$.pipe(
          filter((isApiCallInProgress) => !isApiCallInProgress),
          take(1),
          debounceTime(0), // wait for state changes propagation
          concatMap(() =>
            combineLatest({
              connections: this.runtimeFacade.connections$,
              elements: this.elementsFacade.elements$,
              layoutEntities: this.elementsFacade.layoutEntities$,
              layoutIds: this.elementsFacade.layoutIds$,
              offSyncLayouts: this.elementsFacade.offSyncLayouts$,
              navMenuItems: this.navMenuItemsService.layoutItems$,
              isDragging: this.isDragging$$,
              isRestoringHistory: this.layoutHistory.isRestoring$,
              isGeneratingAILayout: this.store.select(runtimeSelectors.selectIsGeneratingAILayout),
            }).pipe(
              filter(
                ({ isDragging, isRestoringHistory, isGeneratingAILayout }) =>
                  !isDragging && !isRestoringHistory && !isGeneratingAILayout,
              ),
              take(1),
              concatLatestFrom(() => [
                this.elementStylesService.allSheets$,
                this.store.select(runtimeSelectors.selectPages),
              ]),
              filter(([{ elements }]) => {
                try {
                  validateLayout(elements);
                  return true;
                } catch (e) {
                  if (e instanceof LayoutValidationError) {
                    // this.notificationService.error(e.message);
                    sentryLog(`[Layout Validation] ${e.message}`, 'error');
                  }

                  // TODO: change back to false
                  // for now, allow to save the layout
                  return true;
                }
              }),
              concatMap(
                ([
                  {
                    connections,
                    elements,
                    layoutEntities,
                    layoutIds,
                    offSyncLayouts,
                    navMenuItems,
                  },
                  styleSheets,
                  projectPages,
                ]) => {
                  const layoutsToSave = calcLayoutSaveData({
                    elements,
                    connections,
                    layoutIds,
                    layoutEntities,
                    offSyncLayouts,
                    navMenuItems,
                    styleSheets,
                    projectPages,
                    hasUsersAccessRoles: !!this.userAccessRolesService.usersAccessRolesColumn(),
                    getPropsExtractor: this.getPropsExtractorFn,
                  });
                  if (!layoutsToSave.layouts.length) {
                    return of({ value: true, syncId });
                  }

                  layoutsToSave.layouts.forEach((layout) => {
                    this.styleSheetsFacade.updateReferences(layout.pageId, layout.styles);
                  });

                  return this.syncWithApi(layoutsToSave, true).pipe(
                    concatMap((result) => {
                      this.store.dispatch(result);
                      return EMPTY;
                    }),
                    defaultIfEmpty({ value: true, syncId }),
                  );
                },
              ),
              catchError((err) => {
                console.log(`Triggered sync error ocurred`, err);
                return of({ value: false, syncId: undefined });
              }),
            ),
          ),
          finalize(() => this.isManualSyncing$$.next(false)),
          share(),
        ),
      ),
    );
  }

  registerCanvasDraggingDetector(isDragging$: Observable<boolean>): void {
    isDragging$.subscribe(this.isDragging$$);
  }

  syncLayout(): OperatorFunction<LayoutSyncPayload, Action> {
    return (input$: Observable<LayoutSyncPayload>) =>
      input$.pipe(
        debounceTime(SYNC_DEBOUNCE_TIME),
        concatLatestFrom(() => [
          this.elementStylesService.allSheets$,
          this.store.select(runtimeSelectors.selectPages),
        ]),
        map(
          ([
            { elements, connections, layoutIds, layoutEntities, offSyncLayouts, navMenuItems },
            styleSheets,
            projectPages,
          ]) => {
            return calcLayoutSaveData({
              elements,
              connections,
              layoutIds,
              layoutEntities,
              offSyncLayouts,
              navMenuItems,
              styleSheets,
              projectPages,
              hasUsersAccessRoles: !!this.userAccessRolesService.usersAccessRolesColumn(),
              getPropsExtractor: this.getPropsExtractorFn,
            });
          },
        ),
        filter((layoutsToSave) => layoutsToSave.layouts.length > 0),
        takeUntil(
          race(
            this.isManualSyncing$$.pipe(filter((isSyncing) => !!isSyncing)),
            this.layoutHistory.isRestoring$.pipe(filter((isRestoring) => !!isRestoring)),
            this.store
              .select(runtimeSelectors.selectIsGeneratingAILayout)
              .pipe(filter((generating) => !!generating)),
          ).pipe(
            tap(() => {
              logPageLayout('LayoutSyncService', 'syncLayout', (tag) => {
                console.log(`${tag} Layout syncing stopped`);
              });
            }),
          ),
        ),
        tap((layoutsToSave) => {
          layoutsToSave.layouts.forEach((layout) => {
            this.styleSheetsFacade.updateReferences(layout.pageId, layout.styles);
          });
        }),
        concatMap((layoutsToSave) => {
          return this.syncWithApi(layoutsToSave, false);
        }),
        catchError((err) =>
          this.isManualSyncing$.pipe(
            first(),
            concatMap((isManualSyncing) => {
              if (isManualSyncing) {
                return EMPTY;
              }
              throw new Error(err);
            }),
          ),
        ),
        // Retry if saving fails (server/connection error)
        retry({ delay: SYNC_RETRY_INTERVAL }),
      );
  }

  /**
   * @returns {boolean} true - when successfully synced or nothing to sync, false - sync failed
   */
  triggerLayoutSync(): Observable<boolean> {
    return defer(() => {
      const callId = crypto.randomUUID();
      setTimeout(() => this.manualSync$$.next(callId));
      return this.manualSync$.pipe(
        filter(({ syncId }) => syncId === undefined || syncId === callId),
        take(1),
        map(({ value }) => value),
      );
    });
  }

  private syncWithApi(
    layoutSavingData: LayoutSavingData,
    isManualTrigger: boolean,
  ): Observable<Action> {
    return defer(() => {
      this.isApiCallInProgress$$.next(true);

      return this.store.select(runtimeSelectors.selectProjectId).pipe(
        first(),
        switchMap((projectId) => {
          const { layouts, workflowsToRemove } = layoutSavingData;
          return this.projectsApiService.postViewBatchV2(projectId, {
            layouts,
            pages: [], // left empty until it's needed to update pages here
            workflowsToRemove,
          });
        }),
        tap({
          subscribe: () =>
            console.log(`[Layout Sync] Trying to sync (${isManualTrigger})`, layoutSavingData),
          next: (response) => {
            const failedLayoutIds: string[] = response.layoutErrors
              ?.filter(
                (err) =>
                  err.error.ErrorCode === 'LayoutOutdated' ||
                  err.error.ErrorMessage === 'This layout version is outdated.',
              )
              .map((err) => err.pageId)
              .filter(Boolean);

            if (failedLayoutIds.length > 0) {
              this.store.dispatch(fromElements.markLayoutsOffSync({ layoutsIds: failedLayoutIds }));
            } else {
              console.log(`[Layout Sync] Synced (${isManualTrigger})`, response);
            }
          },
          error: (err) => {
            console.error('[Layout Sync] Error saving layout', err);
            this.notificationService.error(err);
          },
        }),
        withLatestFrom(
          this.elementsFacade.elements$,
          this.runtimeFacade.connections$,
          this.elementsFacade.offSyncLayouts$,
          this.navMenuItemsService.layoutItems$,
          this.elementStylesService.allSheets$,
          this.store.select(runtimeSelectors.selectPages),
        ),

        mergeMap(
          ([
            response,
            elements,
            connections,
            offSyncLayouts,
            navMenuItems,
            styleSheetEntities,
            projectPages,
          ]) => {
            return getSyncLayoutUpdateActions(
              response,
              elements,
              connections,
              layoutSavingData,
              offSyncLayouts,
              navMenuItems,
              styleSheetEntities,
              projectPages,
              !!this.userAccessRolesService.usersAccessRolesColumn(),
              this.getPropsExtractorFn,
            );
          },
        ),
        finalize(() =>
          setTimeout(() => {
            return this.isApiCallInProgress$$.next(false);
          }),
        ),
      );
    });
  }
}

/**
 * Returns layout update actions based on the state directly after the save response came from the server.
 *
 * This is an important step so the changes made by the user while the request was in progress are not overwritten
 * and another save request should be sent in a short while.
 *
 * @param response result of saving the layouts on the server
 * @param elements the current state of elements (there might have been some changes while the request was being sent)
 * @param savedLayouts the input what was sent to the server
 * @returns actions to be dispatched
 */
export function getSyncLayoutUpdateActions(
  updateResponse: UpdateProjectViewResponseV2,
  elements: CanvasElement[],
  connections: Connection[],
  savedLayoutData: LayoutSavingData,
  offSyncLayouts: Record<CanvasNamespaceId, boolean>,
  navMenuItems: NavMenu.LegacySharedItem[],
  styleSheets: StyleSheet[],
  projectPages: PageInfo[],
  hasUsersAccessRoles: boolean,
  getPropsExtractor: GetPropsExtractorFn,
): Action[] {
  const { layouts, workflows } = updateResponse;
  const responseLayoutIds = layouts.map((layout) => layout.pageId);
  const responseLayoutEntities = layouts.reduce(
    (acc, layout) => {
      acc[layout.pageId] = layout as LayoutsApiDataResponse;
      return acc;
    },
    {} as Record<CanvasNamespaceId, LayoutsApiDataResponse>,
  );

  // Calculate updated saving data
  const layoutsToSave = calcLayoutSaveData({
    elements,
    connections,
    layoutIds: responseLayoutIds,
    layoutEntities: responseLayoutEntities,
    offSyncLayouts,
    navMenuItems,
    styleSheets,
    projectPages,
    hasUsersAccessRoles,
    getPropsExtractor,
  });

  // Find all layouts which got saved and are now up to date in both layers (ui and action)
  const upToDateLayouts = savedLayoutData.layouts.filter(
    (layout) => !layoutsToSave.layouts.some(({ pageId }) => pageId === layout.pageId),
  );

  // List of layouts which were not saved
  const failedLayoutIds: string[] = updateResponse.layoutErrors
    .map((err) => err.pageId)
    .filter(Boolean);

  // List of workflows which were successfully removed
  const removedWorkflowsIds = savedLayoutData.workflowsToRemove
    .filter((info) => !failedLayoutIds.includes(info.pageId))
    .map((info) => info.workflowIds)
    .flat();

  // If some layout is still different after save, a change was done in the meantime
  // Don't update the local changes, they'll be saved soon in the next request
  return [
    ...upToDateLayouts.reduce((acc, layout) => {
      if (layout.uiLayer?.properties?.type === NAV_MENU_ITEMS_LAYOUT_TYPE) {
        return acc;
      }

      return acc.concat(
        fromElements.replaceElements({
          ns: layout.pageId,
          elements: responseLayoutEntities[layout.pageId].uiLayer.content,
        }),
        runtimeActions.connectionsReplace({
          ns: layout.pageId,
          connections: responseLayoutEntities[layout.pageId].actionLayer.connections,
        }),
      );
    }, [] as Action[]),

    // If there are some layouts & workflows saved - update them
    ...(upToDateLayouts?.length > 0 && workflows?.length > 0
      ? [runtimeActions.workflowsUpdate({ workflows })]
      : []),

    runtimeActions.workflowsRemoved({ workflowIds: removedWorkflowsIds }),

    // Update saved layouts
    fromElements.layoutsSaved({ layouts: layouts as LayoutData[] }),
  ];
}
